import React, { useState, useEffect, useContext } from 'react';
import { Modal } from 'react-bootstrap';
import ActionButtons from '../../crm/shared/field_inputs/shared/ActionButtons';
import { DataContext as SharedDataContext } from '../../../stores/crm-store';
import ExlMailCalendarIcons from '../icons/ExlMailCalendarIcons';
import { useTranslation } from 'react-i18next';
import { GENERAL_ERROR_MESSAGE, GENERAL_ERROR_CONTACT_MESSAGE } from '../../../i18n/keyConstants';

const ExlFormModal = ({
    description,
    defaultFormValues,
    handleAdd,
    onSuccessiveSave,
    onValueSet,
    callback,
    open,
    onClose,
    defaultFormComponents,
    trueText,
    falseText,
    iconBool
}) => {
    const { t } = useTranslation(['errorMessages']);

    // ------------------------ States
    const [isSaving, setIsSaving] = useState(false);
    const [formValues, setFormValues] = useState(defaultFormValues ?? {});
    const [errorMessages, setErrorMessages] = useState({});
    const [formComponents, setFormComponents] = useState(defaultFormComponents);
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);


    const setFormValue = (propName, value) => {
        const newValues = { ...formValues };
        newValues[propName] = value;
        formComponents.forEach((c) => {
            if (!c.bindOnChange) return;
            const boundChange = c.bindOnChange(newValues, propName);
            if (boundChange) {
                newValues[c.propName] = boundChange;
            }
        });

        if (onValueSet) {
            onValueSet(propName, newValues[propName]);
        }
        setFormValues(newValues);
    };
    const setErrorMessage = (propName, e) => {
        errorMessages[propName] = e;
        setErrorMessages({ ...errorMessages });
    };

    const checkWindowSize = () => {
        setIsMobile(window.innerWidth < 575);
        setIsTablet(window.innerWidth > 760 && window.innerWidth < 1024)
    }

    useEffect(() => {
        window.addEventListener('resize', (() => checkWindowSize));
        checkWindowSize();

        return () => {
            window.removeEventListener('resize', (() => checkWindowSize));
        }
    }, [window.innerWidth]);

    useEffect(() => {
        setFormValues(defaultFormValues ?? {});
        setFormComponents([...defaultFormComponents]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFormValues]);

    useEffect(() => {
        if (callback) {
            setFormComponents(callback(formValues, defaultFormComponents))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues]);

    const { store, dispatch } = useContext(SharedDataContext);

    // ------------------------ Methods
    const renderModalFormComponent = (component, formValues, setFormValue, errorMessages) => {
        const errorMessage = errorMessages[component.propName];
        const isInvalid = errorMessage && errorMessage.length;
        return (
            <div key={component.propName}>
                {component.visible !== false ?
                    <div>
                        {component.label ? <label>{component.label}</label> : <div style={{ paddingTop: '.3rem' }} />}
                        {React.cloneElement(component.component, {
                            isInvalid,
                            value: formValues[component.propName],
                            setValue: (v) => setFormValue(component.propName, v),
                        })}
                        {isInvalid && <div className='crm-invalid-msg'>{errorMessage}</div>}
                    </div> : null}
            </div>
        );
    };

    const clearForm = () => {
        setFormValues({ ...defaultFormValues });
        setErrorMessages({});
    };

    const onSave = async () => {
        if (isSaving) return;
        setIsSaving(true);

        const item = { ...formValues };

        // E.g let item.pipelineId = item.pipeline.id
        Object.keys(item).forEach((propName) => {
            if (item[propName]?.id !== undefined) {
                item[propName + 'Id'] = item[propName].id;
            }
        });

        let isInvalid = false;
        formComponents.forEach((c) => {
            if (c.validate) {
                const msg = c.validate(item[c.propName], item);
                setErrorMessage(c.propName, msg);
                if (msg && msg.length) {
                    isInvalid = true;
                }
            }
        });

        if (isInvalid) {
            setIsSaving(false);
            return;
        }

        const response = await handleAdd(item);
        
        setIsSaving(false);
        if (response.success) {
            await new Promise((r) => setTimeout(r, 200));
            dispatch({ type: 'SHOW_SAVED_TOAST' });

            if (!!onSuccessiveSave) {
                onSuccessiveSave(response);
                onClose();
            }
        } else {
            dispatch({ type: 'SHOW_ERROR_TOAST' });
            alert(`${t(GENERAL_ERROR_MESSAGE)}:\n${response.error ?? response.message ?? t(GENERAL_ERROR_CONTACT_MESSAGE)}`);
        }
    };

    const onCancel = () => {
        clearForm();
        onClose()
    };


    return (
        <>
        {!isMobile ? (
            <Modal show={open} onHide={onCancel} className={'exl-modal'}>
                <div className='d-flex justify-content-between align-items-center padding-top-20px padding-left-20px padding-right-20px'>
                    <h4 className='margin-none'>{description}</h4>
                    <button type='button' className='btn btn-secondary crm-modal-btn ' onClick={onCancel}>
                        &times;
                    </button>
                </div>
                <div className='add-item-form container'>
                    <Modal.Body className="container">
                        <div className="row">
                            <div className="col-3 field-input-column">
                                {formComponents && formComponents.filter(function (c) {
                                    if (c.propName === 'description')
                                        return false;
                                    return true;
                                }).map((c) => {
                                    return renderModalFormComponent(c, formValues, setFormValue, errorMessages);
                                })}
                            </div>
                            <div className="col-9 desc-input-column">
                                {formComponents && formComponents.filter(function (c) {
                                    if (c.propName !== 'description')
                                        return false;
                                    return true;
                                }).map((c) => {
                                    return renderModalFormComponent(c, formValues, setFormValue, errorMessages);
                                })}
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                    {formValues?.sendEmailNotifications !== undefined ? (
                        <ExlMailCalendarIcons 
                            sendEmailNotifications={formValues.sendEmailNotifications} 
                            trueText={trueText}
                            falseText={falseText}
                          />
                    ) : formValues?.receiveEmailNotifications !== undefined ? (
                        <ExlMailCalendarIcons 
                            sendEmailNotifications={formValues.receiveEmailNotifications} 
                            trueText={trueText}
                            falseText={falseText}
                          />
                    ) : iconBool !== undefined && (
                        <ExlMailCalendarIcons
                            sendEmailNotifications={iconBool}
                            trueText={trueText}
                            falseText={falseText}
                          />
                    )}
                        <ActionButtons {...{ onCancel, onSave, isSaving }} />
                    </Modal.Footer>
                </div>
            </Modal>
        ) : (
            <Modal show={open} onHide={onCancel} className={'exl-modal'}>
            <div className='d-flex justify-content-between align-items-center padding-top-20px padding-left-20px padding-right-20px'>
                <h4 className='margin-none'>{description}</h4>
                <button type='button' className='btn btn-secondary crm-modal-btn ' onClick={onCancel}>
                    &times;
                </button>
            </div>
            <div className='add-item-form container'>
                <Modal.Body className="container">
                    <div className="row">
                        <div className="col-9 field-input-column">
                            {formComponents && formComponents.map((c) => {
                                return renderModalFormComponent(c, formValues, setFormValue, errorMessages);
                            })}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="exl-modal-footer">
                {formValues?.sendEmailNotifications !== undefined ? (
                    <ExlMailCalendarIcons 
                        sendEmailNotifications={formValues.sendEmailNotifications} 
                        trueText={trueText}
                        falseText={falseText}
                      />
                ):formValues?.receiveEmailNotifications !== undefined ? (
                    <ExlMailCalendarIcons 
                        sendEmailNotifications={formValues.receiveEmailNotifications} 
                        trueText={trueText}
                        falseText={falseText}
                      />
                )  : iconBool !== undefined && (
                    <ExlMailCalendarIcons
                        sendEmailNotifications={iconBool}
                        trueText={trueText}
                        falseText={falseText}
                      />
                )}
                <ActionButtons {...{ onCancel, onSave, isSaving }} />
                </Modal.Footer>
            </div>
        </Modal>
        )}
    </>
    );
};

export default ExlFormModal;
